// ══════════════════════════════════════════════════════════════
// COMPASS – Notification Service
// ══════════════════════════════════════════════════════════════
import { inject, Injectable } from '@angular/core';
import { ToastrService, IndividualConfig, ActiveToast } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { CustomToastComponent } from './components/custom-toast/custom-toast.component';
import { NotificationConfig, NotificationType } from './notification.model';

/** Shape of our i18n keys (e.g. PROCESS.ERROR.LOAD_PROCESS); backend sentences never match. */
const TRANSLATION_KEY = /^[A-Z][A-Z0-9_]*(\.[A-Za-z0-9_]+)+$/;

const DEFAULTS: Partial<IndividualConfig> = {
  timeOut: 5000,
  toastComponent: CustomToastComponent,
  toastClass: '',       // we handle styling inside the component
  closeButton: false,   // the component has its own close
  tapToDismiss: false,
};

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly toastr = inject(ToastrService);
  private readonly translate = inject(TranslateService);

  // ── Convenience methods ──

  success(message: string, title?: string, config?: Partial<NotificationConfig>): void {
    this.show({ message, title, type: 'success', ...config });
  }

  info(message: string, title?: string, config?: Partial<NotificationConfig>): void {
    this.show({ message, title, type: 'info', ...config });
  }

  error(message: string, title?: string, config?: Partial<NotificationConfig>): void {
    this.show({ message, title, type: 'error', duration: 0, showClose: true, ...config });
  }

  warning(message: string, title?: string, config?: Partial<NotificationConfig>): void {
    this.show({ message, title, type: 'warning', ...config });
  }

  // ── Core show ──

  /** Returns the toast shown (its `onHidden` tells when it closes), or null when an identical one is already open. */
  show(config: NotificationConfig): ActiveToast<unknown> | null {
    const toastrType = this.mapType(config.type);
    const message = this.text(config.message, config.messageParams);
    const title = config.title ? this.text(config.title) : '';

    const overrides: Partial<IndividualConfig> = {
      ...DEFAULTS,
      timeOut: config.duration ?? 5000,
    };

    const ref = this.toastr.show(
      message,
      title,
      overrides,
      toastrType
    );

    // Inject custom data into the component instance
    if (ref?.toastRef?.componentInstance) {
      const instance = ref.toastRef.componentInstance as CustomToastComponent;
      instance.notificationType = config.type;
      instance.showCloseButton = config.showClose ?? true;

      if (config.action) {
        instance.actionLabel = this.text(config.action.label);
        instance.actionCallback = config.action.callback;
      }
    }
    return ref ?? null;
  }

  // ── Helpers ──

  /** Translates i18n keys; backend messages arrive already localized and are shown as-is. */
  private text(value: string, params?: Record<string, unknown>): string {
    return TRANSLATION_KEY.test(value) ? this.translate.instant(value, params) : value;
  }

  private mapType(type: NotificationType): string {
    const map: Record<NotificationType, string> = {
      success: 'toast-success',
      info: 'toast-info',
      error: 'toast-error',
      warning: 'toast-warning',
    };
    return map[type];
  }
}
